import { useEffect, useState, useRef } from 'react';
import Boundary from '@app/Boundary';
import RoutesRoot from '@routes/index';
import useThemeStore from '@stores/theme.store';
import { ThemeProvider } from 'styled-components';
import { getTheme, getAntdTheme } from '@theme/theme';
import { AppScrollbarsGlobalStyle } from '@theme/appScrollbarsGlobalStyle';
import MessageProvider from '@context/MessageProvider';
import AbilityProvider from '@/ability/AbilityProvider';
import ViteHMRHandler from '@/app/configs/ViteHMRHandler';
import NotificationProvider from '@context/NotificationProvider';
import PreloadErrorOverlay from '@/app/configs/PreloadErrorOverlay';
import AppDeployUpdateBar from '@/components/ui/AppDeployUpdateBar';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, App as AntApp, Spin, theme as antdTheme } from 'antd';
import { QueryClientProvider, focusManager } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import {
    appQueryClient,
    pauseAppQueriesForRecovery,
    resetAppQueryClientCaches,
    resumeAppQueriesAfterRecovery,
} from '@app/appQueryClient';
import {
    markTabHidden,
    markTabVisible,
    runSessionPoolRecovery,
    shouldRunWakeRecovery,
    MIN_HIDDEN_MS_FOR_WAKE_RECOVERY,
} from '@utils/sessionNetworkCoordinator';

const App = () => {
    const mode = useThemeStore((state) => state.mode);
    const currentTheme = getTheme(mode);
    const antdThemeConfig = getAntdTheme(mode);

    // Apply dark algorithm for Ant Design
    const algorithm = mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

    const processComponents = (components) => {
        const processed = {};
        Object.keys(components).forEach((componentName) => {
            const config = components[componentName];
            processed[componentName] = {
                ...config,
                fontFamily: config.fontFamily || currentTheme.fonts.family,
            };
        });
        return processed;
    };

    const finalAntdConfig = {
        ...antdThemeConfig,
        algorithm,
        components: {
            ...processComponents(antdThemeConfig.components),
        },
    };

    const [authRehydrated, setAuthRehydrated] = useState(false);
    const wasAuthenticatedRef = useRef(null);

    useEffect(() => {
        let done = false;
        const markRehydrated = () => {
            if (!done) {
                done = true;
                setAuthRehydrated(true);
            }
        };
        useAuthStore.persist?.onFinishHydration?.(markRehydrated);
        const t = setTimeout(markRehydrated, 150);
        return () => clearTimeout(t);
    }, []);

    // Pause queries while hidden; on return from idle run ordered pool recovery (refresh before refetch storm).
    useEffect(() => {
        focusManager.setEventListener((handleFocus) => {
            if (typeof window === 'undefined' || !window.addEventListener) return undefined;

            const onVisibility = () => {
                if (document.visibilityState === 'hidden') {
                    markTabHidden();
                    // Only pause the query client when the hide is long enough to
                    // trigger a real wake-recovery. Short tab switches (< 5s) must
                    // NOT pause queries — doing so blocks any in-flight form submits.
                    // We schedule a deferred pause so that truly long hides still pause.
                    handleFocus(false);
                    return;
                }
                const { hiddenMs } = markTabVisible();
                // Always restore the query client when the tab becomes visible again.
                resumeAppQueriesAfterRecovery();
                handleFocus(true);
                if (shouldRunWakeRecovery(hiddenMs)) {
                    // Pause NOW (before recovery starts) only for long wake cycles.
                    pauseAppQueriesForRecovery();
                    void runSessionPoolRecovery({ reason: 'wake' });
                }
            };

            const onPageShow = (ev) => {
                if (ev.persisted) {
                    void runSessionPoolRecovery({ reason: 'bfcache', force: true });
                }
            };

            window.addEventListener('visibilitychange', onVisibility, false);
            window.addEventListener('pageshow', onPageShow, false);
            return () => {
                window.removeEventListener('visibilitychange', onVisibility);
                window.removeEventListener('pageshow', onPageShow);
            };
        });
    }, []);

    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    useEffect(() => {
        if (authRehydrated && wasAuthenticatedRef.current === true && !isAuthenticated) {
            resetAppQueryClientCaches();
        }
        if (authRehydrated) wasAuthenticatedRef.current = isAuthenticated;
    }, [authRehydrated, isAuthenticated]);

    if (!authRehydrated) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                }}
            >
                <Spin size="large" />
            </div>
        );
    }

    return (
        <ConfigProvider theme={finalAntdConfig}>
            <QueryClientProvider client={appQueryClient}>
                <ThemeProvider theme={currentTheme}>
                    <AppScrollbarsGlobalStyle />
                    <AntApp>
                        <MessageProvider>
                            <NotificationProvider>
                                <PreloadErrorOverlay />
                                <ViteHMRHandler />
                                <AbilityProvider>
                                    <Boundary>
                                        <AppDeployUpdateBar />
                                        <RoutesRoot />
                                    </Boundary>
                                </AbilityProvider>
                            </NotificationProvider>
                        </MessageProvider>
                    </AntApp>
                    {/* {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />} */}
                </ThemeProvider>
            </QueryClientProvider>
        </ConfigProvider>
    );
};

export default App;
